import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import i18n from "@i18n";
import { getActiveUser } from "@noom/hive-auth";
import { getNonTerminalTreatments } from "src/funnels/insurance-reimbursement/requests/getNonTerminalTreatments";
import { useServerContext } from "src/hooks/redux";
import { nonTerminalTreatmentsQueryKey } from "src/hooks/telehealth/useNonTerminalTreatments";
import { getProduct } from "src/utils/api/productCatalog";
import {
  ACTIVE_STATUSES,
  searchSubscriptions,
} from "src/utils/api/subscriptions";
import type { TreatmentSummaryProto } from "@noom/noom-contracts/noom_contracts/healthcare/model/treatment";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import {
  buildCancellationMedication,
  CancellationMedication,
  findMembershipLinkedTreatment,
  findSubscriptionTreatment,
  getMedicationTreatmentCandidates,
} from "src/utils/cancellationMedication";
import { captureException } from "src/utils/error";
import { queryClient } from "src/utils/fetch";
import { isSubscriptionMedication } from "src/utils/medication";
import { isSubscriptionMembership } from "src/utils/membership";
import { trackTask } from "src/utils/monitoring/events/tasks";
import getStore, { CoreReduxState } from "src/utils/redux/store";

/** Longest the cancellation pages hold their loader before rendering without the medication. */
export const LOADER_CAP_MS = 3000;

// Keeps the pages from sitting on a loader through react-query's default
// backoff; on failure they fall back to the no-medication copy.
const QUERY_OPTIONS = { retry: 1 };

type CancellationFlow = "medication" | "membership";

/** The cancellation flow that shows a medication, or undefined for every other flow. */
function getCancellationFlow(
  state: CoreReduxState
): CancellationFlow | undefined {
  if (isSubscriptionMedication(state)) return "medication";
  if (isSubscriptionMembership(state)) return "membership";
  return undefined;
}

async function getTreatments(accessCode: string) {
  const { treatments = [] } = await queryClient.fetchQuery(
    nonTerminalTreatmentsQueryKey(accessCode),
    () => getNonTerminalTreatments(accessCode)
  );
  return treatments;
}

// Best-effort: the card still renders without the dosage.
async function getCatalogEntry(saleItemId: string | undefined) {
  if (!saleItemId) return undefined;
  try {
    return await getProduct(saleItemId);
  } catch (error) {
    captureException(error);
    return undefined;
  }
}

async function buildWithCatalogEntry(treatment: TreatmentSummaryProto) {
  const catalogEntry = await getCatalogEntry(treatment.currentSaleItemId);
  return buildCancellationMedication(treatment, catalogEntry) ?? null;
}

/** Reports a medication the page should have shown but couldn't, to Sentry and Mixpanel. */
function reportUnresolvedMedication(
  errorMessage: string,
  extras: Record<string, unknown>
) {
  captureException(new Error(errorMessage), undefined, extras);
  trackBuyflowEvent("BuyflowActionFailed", { errorMessage });
}

/** Resolves the medication billed by the subscription being cancelled. */
export async function loadSubscriptionMedication(
  accessCode: string,
  subscriptionId: string | undefined
): Promise<CancellationMedication | null> {
  const [treatments, subscriptions] = await Promise.all([
    getTreatments(accessCode),
    searchSubscriptions({ statusFilter: ACTIVE_STATUSES }),
  ]);
  const candidates = getMedicationTreatmentCandidates(treatments);
  const subscription = subscriptions.find((s) => s.id === subscriptionId);
  const treatment = findSubscriptionTreatment(candidates, subscription);
  // Unlike the membership flow, a missing treatment is always unexpected here:
  // the user is cancelling a medication subscription.
  if (!treatment) {
    reportUnresolvedMedication("Unable to resolve cancellation medication", {
      subscriptionId,
      saleItemId: subscription?.saleItemId,
      treatmentCodes: candidates.map((t) => t.treatmentCode),
    });
    return null;
  }
  return buildWithCatalogEntry(treatment);
}

/**
 * Resolves the medication tied to the user's membership: the medication
 * treatment whose parent is the MEMBERSHIP treatment. Users can have other
 * medication treatments (e.g. sermorelin), which aren't linked to it.
 */
export async function loadMembershipMedication(
  accessCode: string
): Promise<CancellationMedication | null> {
  const allTreatments = await getTreatments(accessCode);
  const candidates = getMedicationTreatmentCandidates(allTreatments);
  if (candidates.length === 0) return null;

  const linkedTreatment = findMembershipLinkedTreatment(
    allTreatments,
    candidates
  );
  if (!linkedTreatment) {
    reportUnresolvedMedication("Unable to resolve membership medication", {
      treatmentCodes: candidates.map((t) => t.treatmentCode),
    });
    return null;
  }
  return buildWithCatalogEntry(linkedTreatment);
}

async function trackLoad(
  flow: CancellationFlow,
  load: () => Promise<CancellationMedication | null>
) {
  const task = trackTask(
    "LoadCancellationMedication",
    { flow },
    { delayTimeout: LOADER_CAP_MS }
  );
  task.start();
  try {
    const medication = await load();
    task.success({ resolved: !!medication });
    return medication;
  } catch (error) {
    task.failed(error);
    throw error;
  }
}

/** Returns the query for the given flow, or undefined when there is nothing to load. */
function getCancellationMedicationQuery({
  accessCode,
  flow,
  subscriptionId,
}: {
  accessCode: string | undefined;
  flow: CancellationFlow | undefined;
  subscriptionId: string | undefined;
}) {
  if (!accessCode || !flow) return undefined;
  const load =
    flow === "medication"
      ? () => loadSubscriptionMedication(accessCode, subscriptionId)
      : () => loadMembershipMedication(accessCode);
  return {
    queryKey: ["cancellationMedication", flow, accessCode, subscriptionId],
    queryFn: () => trackLoad(flow, load),
  };
}

/** Warms the cache so the membership or medication cancellation pages render without a loader. */
export function prefetchCancellationMedication() {
  const state = getStore().getState();
  const query = getCancellationMedicationQuery({
    accessCode: getActiveUser()?.accessCode,
    flow: getCancellationFlow(state),
    subscriptionId: state.serverContext.subscription_id,
  });
  if (!query) return;
  queryClient.prefetchQuery(query.queryKey, query.queryFn, QUERY_OPTIONS);
}

/**
 * The medication shown in the membership and medication cancellation flows.
 * `isLoading` turns false after LOADER_CAP_MS even if the query is still running,
 * so pages can't hold their buttons behind a loader; `medication` fills in later.
 * `medicationName` falls back to generic copy while there is no medication.
 */
export default function useCancellationMedication(): {
  isLoading: boolean;
  medication: CancellationMedication | undefined;
  medicationName: string;
} {
  const flow = useSelector(getCancellationFlow);
  const { subscription_id: subscriptionId } = useServerContext();
  const query = getCancellationMedicationQuery({
    accessCode: getActiveUser()?.accessCode,
    flow,
    subscriptionId,
  });
  const { data, isLoading } = useQuery({
    queryKey: query?.queryKey ?? ["cancellationMedication"],
    queryFn: query?.queryFn ?? (async () => null),
    enabled: !!query,
    ...QUERY_OPTIONS,
  });

  const [capExpired, setCapExpired] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setCapExpired(true), LOADER_CAP_MS);
    return () => clearTimeout(timer);
  }, []);

  const medication = data ?? undefined;
  return {
    isLoading: isLoading && !capExpired,
    medication,
    medicationName:
      medication?.name ?? i18n.t("counterOfferSurvey:medicationFallbackName"),
  };
}
